import deepEquals from 'fast-deep-equal';
import { useState, useEffect, useCallback } from 'react';

var storageNamespace = '@useStatePerist:';
var cacheKey = '@useStatePersistCacheKey';

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

var Event = /*#__PURE__*/function () {
  function Event() {
    this.handlers = [];
  }

  var _proto = Event.prototype;

  _proto.on = function on(handler) {
    var _this = this;

    this.handlers.push(handler);
    return function () {
      return _this.off(handler);
    };
  };

  _proto.off = function off(handler) {
    this.handlers = this.handlers.filter(function (h) {
      return h !== handler;
    });
  };

  _proto.trigger = function trigger(data) {
    this.handlers.slice(0).forEach(function (h) {
      return h(data);
    });
  };

  _proto.expose = function expose() {
    return this;
  };

  return Event;
}();

var SyncStorage = /*#__PURE__*/function () {
  function SyncStorage(events) {
    if (events === void 0) {
      events = {};
    }

    this.events = events;
  }

  SyncStorage.getInstance = function getInstance() {
    if (SyncStorage.instance) return SyncStorage.instance;
    return SyncStorage.instance = new SyncStorage();
  };

  var _proto = SyncStorage.prototype;

  _proto.init = function init() {
    return Promise.resolve();
  };

  _proto.subscribe = function subscribe(key, callback) {
    return this.getEvent(key).on(callback);
  };

  _proto.getItem = function getItem(key) {
    var value = localStorage.getItem(key);
    if (!value) return;
    var data = value;

    try {
      data = JSON.parse(value);
    } catch (err) {
      data = value;
    }

    return data;
  };

  _proto.setItem = function setItem(key, value) {
    if (!key) throw Error('No key provided');
    this.getEvent(key).trigger(value);
    localStorage.setItem(key, JSON.stringify(value));
  };

  _proto.getEvent = function getEvent(key) {
    if (this.events[key]) {
      return this.events[key];
    }

    return this.events[key] = new Event();
  };

  _proto.getAllKeys = function getAllKeys() {
    return Object.keys(localStorage);
  };

  _proto.removeItem = function removeItem(key) {
    this.getEvent(key).trigger(undefined);
    localStorage.removeItem(key);
  };

  _proto.clear = function clear() {
    localStorage.clear();
  };

  _createClass(SyncStorage, [{
    key: "length",
    get: function get() {
      return localStorage.length;
    }
  }]);

  return SyncStorage;
}(); // API Compatibility with native


SyncStorage.loaded = true;
var syncStorage = /*#__PURE__*/SyncStorage.getInstance();

var useStatePersist = function useStatePersist(key, initialValue) {
  // Storage namespace
  var _useState = useState(function () {
    try {
      // Get from local storage by key
      var item = syncStorage.getItem(storageNamespace + key); // Get item or else initial value

      var isNotset = item === undefined || item === null;
      return isNotset ? initialValue : item;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  }),
      state = _useState[0],
      setState = _useState[1];

  useEffect(function () {
    var unsubscribe = syncStorage.subscribe(storageNamespace + key, function (data) {
      if (isState(data)) return;
      setState(data);
    });
    return function () {
      return unsubscribe();
    };
  }, []);

  var updateState = function updateState(data) {
    try {
      // Allow value to be a function so we have same API as useState
      var valueToStore = data instanceof Function ? data(state) : data; // Save state

      setState(valueToStore); // Save to local storage

      syncStorage.setItem(storageNamespace + key, valueToStore);
    } catch (error) {
      console.error(error);
    }
  };

  var isState = useCallback(function (compareValue) {
    return deepEquals(state, compareValue);
  }, [state]);
  return [state, updateState];
};

/**
 * Checks if value is a function
 * @param object
 * @returns
 */
function isFunction(object) {
  return !!(object && object.constructor && object.call && object.apply);
}

var invalidateCache = function invalidateCache(invalidateKey) {
  try {
    var _temp3 = function _temp3() {
      return Promise.resolve(checkAndInvalidate(value)).then(function () {});
    };

    var value;

    var _temp4 = function () {
      if (isFunction(invalidateKey)) {
        return Promise.resolve(invalidateKey()).then(function (_invalidateKey) {
          value = _invalidateKey;
        });
      } else {
        value = invalidateKey;
      }
    }();

    return Promise.resolve(_temp4 && _temp4.then ? _temp4.then(_temp3) : _temp3(_temp4));
  } catch (e) {
    return Promise.reject(e);
  }
}; // Need to be async to make sure storage is initialized

var checkAndInvalidate = function checkAndInvalidate(invalidateKey) {
  try {
    return Promise.resolve(syncStorage.init()).then(function () {
      var key = syncStorage.getItem(cacheKey);

      var _temp5 = function () {
        if (invalidateKey !== key) {
          return Promise.resolve(clearState()).then(function () {
            syncStorage.setItem(cacheKey, invalidateKey);
          });
        }
      }();

      if (_temp5 && _temp5.then) return _temp5.then(function () {});
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

var clearState = function clearState() {
  try {
    return Promise.resolve(syncStorage.init()).then(function () {
      var keys = syncStorage.getAllKeys();
      keys.forEach(function (k) {
        if (k.startsWith(storageNamespace)) {
          syncStorage.removeItem(k);
        }
      });
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

export { clearState, invalidateCache, syncStorage, useStatePersist };
//# sourceMappingURL=use-state-persist.esm.js.map
